import { Transaction } from '../../types/api';

import { baseApi } from './baseApi';

export interface GetBalanceResponse {
  status: 'success';
  message: string;
  data: {
    balance: number;
    referralBalance: number;
    cashbackBalance: number;
  };
}

export type PaymentMethod = 'payeer' | 'crypto' | 'syriatel' | 'sham-cash' | 'binance' | 'coinex';

export interface CreateDepositRequest {
  amount: number;
  currency?: 'USD' | 'EUR' | 'SYP';
}

export interface InvoiceDetails {
  invoiceId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  status: 'pending' | 'completed' | 'failed';
  instructions?: string | {
    receptionNumber?: string | string[];
    amount?: number;
    currency?: string;
    orderId?: string;
    message?: string;
  };
  paymentUrl?: string;
  invoiceDetails?: {
    uuid?: string;
    orderId?: string;
    network?: string;
    address?: string;
    amount?: string;
    currency?: string;
  };
  receptionNumber?: string;
  orderId?: string;
  message?: string;
  successUrl?: string;
  failUrl?: string;
}

export interface CreateDepositResponse {
  status: 'success';
  message: string;
  data: {
    invoice: InvoiceDetails;
  };
}

export interface TransactionFilters {
  type?:
    | 'recharge'
    | 'purchase'
    | 'referral_bonus'
    | 'refund'
    | 'admin_add'
    | 'admin_deduct'
    | 'cashback_claim'
    | 'cashback_earn'
    | 'admin_add_referral'
    | 'admin_deduct_referral'
    | 'admin_add_cashback'
    | 'admin_deduct_cashback';
  status?: 'pending' | 'completed' | 'failed' | 'reversed';
  paymentMethod?: 'Payeer' | 'Crypto' | 'Syriatel Cash' | 'Sham Cash';
  page?: number;
  limit?: number;
}

export interface GetTransactionsResponse {
  status: 'success';
  message: string;
  data: {
    transactions: Transaction[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

export interface GetExchangeRateResponse {
  status: 'success';
  message: string;
  data: {
    usdToSypExchangeRate: number;
    syriatelCashExchangeRate: number;
    shamCashExchangeRate: number;
    fromCurrency: string;
    toCurrency: string;
  };
}

export interface ClaimReferralResponse {
  status: 'success';
  message: string;
  data: {
    claimedAmount: number;
    balance: number;
    referralBalance: number;
  };
}

export interface ClaimCashbackResponse {
  status: 'success';
  message: string;
  data: {
    claimedAmount: number;
    balance: number;
    cashbackBalance: number;
  };
}

export interface ClaimSyriatelPaymentRequest {
  internalOrderId: string;
  transactionId: string;
}

export interface ClaimSyriatelPaymentResponse {
  status: 'success';
  message: string;
  data: {
    claimed: boolean;
    amountInUSD?: number;
    transactionId: string;
    internalOrderId: string;
    newBalance?: number;
    paymentStatus?: string;
    balance?: number;
    status?: 'pending_webhook';
    message?: string;
  };
}

export interface RechargeReceptionAddressesResponse {
  status: 'success';
  data: {
    payeer: {
      minAmount: number | null;
    };
    crypto: {
      minAmount: number | null;
    };
    shamCash: {
      receptionNumber: string;
      acceptedCurrencies: ('USD' | 'SYP')[];
      minAmount: number | null;
    };
    syriatelCash: {
      receptionCodes: string[];
      minAmount: number | null;
    };
    binance: {
      walletAddress: string | null;
      minAmount: number;
    };
    coinex: {
      adminEmail: string | null;
      minAmount: number | null;
      coin: string;
    };
    usdToSypExchangeRate: number;
    syriatelCashExchangeRate: number;
    shamCashExchangeRate: number;
  };
}

export interface ClaimShamCashPaymentRequest {
  transactionId: string;
}

export interface ClaimShamCashPaymentResponse {
  status: 'success';
  message: string;
  data: {
    transactionId: string;
    sourceAmount: number;
    sourceCurrency: 'USD' | 'SYP';
    creditedAmountUsd: number;
    balance: number;
  };
}

export interface ClaimBinancePaymentRequest {
  transactionId: string;
}

export interface ClaimBinancePaymentResponse {
  status: 'success';
  message: string;
  data: {
    transactionId: string;
    matchedAmount: number | null;
    currency: string;
    balance: number;
  };
}

// ==================== CoinEx Types ====================

export interface SendCoinExVerificationRequest {
  email: string;
}

export interface SendCoinExVerificationResponse {
  status: 'success';
  message: string;
}

export interface VerifyCoinExEmailRequest {
  email: string;
  code: string;
}

export interface VerifyCoinExEmailResponse {
  status: 'success';
  message: string;
  data: {
    coinex_email: string;
  };
}

export interface CoinExStatusResponse {
  status: 'success';
  data: {
    coinex_email: string | null;
    adminEmail: string | null;
    coin: string;
    minAmount: number | null;
  };
}

export const balanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get User Balance
    getBalance: builder.query<GetBalanceResponse, void>({
      query: () => ({
        url: '/users/me/balance',
        method: 'GET',
      }),
      providesTags: ['Balance'],
    }),

    // Create Deposit Invoice
    createDeposit: builder.mutation<
      CreateDepositResponse,
      { paymentMethod: PaymentMethod; data: CreateDepositRequest }
    >({
      query: ({ paymentMethod, data }) => ({
        url: `/recharge/${paymentMethod}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // Get current user's transaction history (me/transactions) – اسم فريد لتفادي التعارض مع admin getTransactions
    getMyTransactions: builder.query<GetTransactionsResponse, TransactionFilters>({
      query: (filters) => {
        const params = new URLSearchParams();
        
        if (filters.type) params.append('type', filters.type);
        if (filters.status) params.append('status', filters.status);
        if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());

        return {
          url: `/users/me/transactions${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Transactions'],
    }),

    // Get Exchange Rate (Public API)
    // GET /api/v1/exchange-rate
    // This is a public endpoint that does not require authentication
    // Returns: { status: "success", data: { usdToSypExchangeRate: number, fromCurrency: "USD", toCurrency: "SYP" } }
    getExchangeRatePublic: builder.query<GetExchangeRateResponse, void>({
      query: () => ({
        url: '/exchange-rate',
        method: 'GET',
      }),
    }),

    // Claim Referral Earnings
    claimReferral: builder.mutation<ClaimReferralResponse, void>({
      query: () => ({
        url: '/users/me/claim-referral',
        method: 'POST',
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // Claim Cashback Earnings
    claimCashback: builder.mutation<ClaimCashbackResponse, void>({
      query: () => ({
        url: '/users/me/claim-cashback',
        method: 'POST',
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // Claim Syriatel Payment
    claimSyriatelPayment: builder.mutation<ClaimSyriatelPaymentResponse, ClaimSyriatelPaymentRequest>({
      query: (data) => ({
        url: '/recharge/syriatel/claim',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // Get recharge reception addresses (Sham Cash + Syriatel Cash)
    getRechargeReceptionAddresses: builder.query<RechargeReceptionAddressesResponse, void>({
      query: () => ({
        url: '/recharge/reception-addresses',
        method: 'GET',
      }),
    }),

    // Claim Sham Cash payment
    claimShamCashPayment: builder.mutation<ClaimShamCashPaymentResponse, ClaimShamCashPaymentRequest>({
      query: (data) => ({
        url: '/recharge/sham-cash/claim',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // Claim Binance payment
    claimBinancePayment: builder.mutation<ClaimBinancePaymentResponse, ClaimBinancePaymentRequest>({
      query: (data) => ({
        url: '/recharge/binance/claim',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Balance', 'Transactions'],
    }),

    // CoinEx: Send verification code to email
    sendCoinExVerification: builder.mutation<SendCoinExVerificationResponse, SendCoinExVerificationRequest>({
      query: (data) => ({
        url: '/recharge/coinex/send-verification',
        method: 'POST',
        body: data,
      }),
    }),

    // CoinEx: Verify email code
    verifyCoinExEmail: builder.mutation<VerifyCoinExEmailResponse, VerifyCoinExEmailRequest>({
      query: (data) => ({
        url: '/recharge/coinex/verify-email',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Balance'],
    }),

    // CoinEx: Get status
    getCoinExStatus: builder.query<CoinExStatusResponse, void>({
      query: () => ({
        url: '/recharge/coinex/status',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetBalanceQuery,
  useCreateDepositMutation,
  useGetMyTransactionsQuery,
  useGetExchangeRatePublicQuery,
  useClaimReferralMutation,
  useClaimCashbackMutation,
  useClaimSyriatelPaymentMutation,
  useGetRechargeReceptionAddressesQuery,
  useClaimShamCashPaymentMutation,
  useClaimBinancePaymentMutation,
  useSendCoinExVerificationMutation,
  useVerifyCoinExEmailMutation,
  useGetCoinExStatusQuery,
} = balanceApi;

